/**
 * Canvas 서비스
 * Canvas 기반 레이아웃 디자이너 비즈니스 로직
 */

import type { Point, CanvasZone, CanvasSeat, CanvasConfig } from '../models/Canvas';

export class CanvasService {
  // 구역 색상 팔레트
  private static readonly ZONE_COLORS = [
    '#3B82F6', // blue
    '#10B981', // green
    '#F59E0B', // amber
    '#EF4444', // red
    '#8B5CF6', // purple
    '#06B6D4', // cyan
    '#84CC16', // lime
    '#F97316', // orange
  ];

  // 캔버스 좌표 계산
  getCanvasPoint(event: React.MouseEvent, canvas: HTMLCanvasElement, scale: number, offset: Point): Point {
    const rect = canvas.getBoundingClientRect();
    const x = (event.clientX - rect.left) / scale - offset.x;
    const y = (event.clientY - rect.top) / scale - offset.y;
    return { x, y };
  }

  // 격자에 맞춘 좌표
  snapToGrid(point: Point, gridSize: number): Point {
    return {
      x: Math.round(point.x / gridSize) * gridSize,
      y: Math.round(point.y / gridSize) * gridSize,
    };
  }

  // 구역 생성
  createZone(startPoint: Point, currentPoint: Point, zoneCount: number): CanvasZone | null {
    const x = Math.min(startPoint.x, currentPoint.x);
    const y = Math.min(startPoint.y, currentPoint.y);
    const width = Math.abs(currentPoint.x - startPoint.x);
    const height = Math.abs(currentPoint.y - startPoint.y);

    // 최소 크기 확인
    if (width <= 40 || height <= 40) {
      return null;
    }

    return {
      id: Date.now().toString(),
      name: `구역 ${zoneCount + 1}`,
      x,
      y,
      width,
      height,
      color: CanvasService.ZONE_COLORS[zoneCount % CanvasService.ZONE_COLORS.length],
    };
  }

  // 좌석 생성
  createSeat(
    point: Point,
    selectedZone: string,
    zones: CanvasZone[],
    existingSeats: CanvasSeat[]
  ): CanvasSeat | null {
    const zone = zones.find((z) => z.id === selectedZone);
    if (!zone) return null;

    // 좌석이 구역 내부인지 확인
    if (point.x < zone.x || point.x > zone.x + zone.width || point.y < zone.y || point.y > zone.y + zone.height) {
      return null;
    }

    const zoneIndex = zones.findIndex((z) => z.id === selectedZone);
    const seatCountInZone = existingSeats.filter((s) => s.zoneId === selectedZone).length;

    return {
      id: `seat-${Date.now()}`,
      number: `${zoneIndex + 1}-${String(seatCountInZone + 1).padStart(2, '0')}`,
      x: point.x,
      y: point.y,
      width: 30,
      height: 30,
      zoneId: selectedZone,
      type: 'standard',
      rotation: 0,
    };
  }

  // 자동 좌석 배치
  autoArrangeSeats(zone: CanvasZone): CanvasSeat[] {
    const seatWidth = 30;
    const seatHeight = 30;
    const spacing = 10;
    const margin = 20;

    const seatsPerRow = Math.floor((zone.width - margin * 2 + spacing) / (seatWidth + spacing));
    const seatsPerCol = Math.floor((zone.height - margin * 2 + spacing) / (seatHeight + spacing));

    const newSeats: CanvasSeat[] = [];
    let seatNumber = 1;

    for (let row = 0; row < seatsPerCol; row++) {
      for (let col = 0; col < seatsPerRow; col++) {
        newSeats.push({
          id: `${zone.id}-seat-${seatNumber}`,
          number: `${zone.name.slice(-1)}-${seatNumber.toString().padStart(2, '0')}`,
          x: zone.x + margin + col * (seatWidth + spacing),
          y: zone.y + margin + row * (seatHeight + spacing),
          width: seatWidth,
          height: seatHeight,
          zoneId: zone.id,
          type: 'standard',
          rotation: 0,
        });
        seatNumber++;
      }
    }

    return newSeats;
  }

  // 좌석 회전
  rotateSeat(seat: CanvasSeat): CanvasSeat {
    return {
      ...seat,
      rotation: (seat.rotation + 90) % 360,
    };
  }

  // 객체 선택 확인 (포인트가 객체 내부에 있는지)
  isPointInZone(point: Point, zone: CanvasZone): boolean {
    return point.x >= zone.x && point.x <= zone.x + zone.width && point.y >= zone.y && point.y <= zone.y + zone.height;
  }

  isPointInSeat(point: Point, seat: CanvasSeat): boolean {
    return point.x >= seat.x && point.x <= seat.x + seat.width && point.y >= seat.y && point.y <= seat.y + seat.height;
  }

  // 구역 이동 시 좌석들도 함께 이동
  moveZoneWithSeats(zone: CanvasZone, newX: number, newY: number, seats: CanvasSeat[]): CanvasSeat[] {
    const deltaX = newX - zone.x;
    const deltaY = newY - zone.y;

    return seats.map((seat) => (seat.zoneId === zone.id ? { ...seat, x: seat.x + deltaX, y: seat.y + deltaY } : seat));
  }

  // 레이아웃 데이터 검증
  validateLayoutData(zones: CanvasZone[], seats: CanvasSeat[]): { valid: boolean; message?: string } {
    // 구역이 없으면 경고
    if (zones.length === 0) {
      return { valid: false, message: '최소 1개 이상의 구역이 필요합니다.' };
    }

    // 좌석이 없으면 경고
    if (seats.length === 0) {
      return { valid: false, message: '최소 1개 이상의 좌석이 필요합니다.' };
    }

    // 모든 좌석이 유효한 구역에 속하는지 확인
    const invalidSeats = seats.filter((seat) => !zones.find((zone) => zone.id === seat.zoneId));
    if (invalidSeats.length > 0) {
      return { valid: false, message: `${invalidSeats.length}개의 좌석이 유효하지 않은 구역에 속해 있습니다.` };
    }

    return { valid: true };
  }
}

export const canvasService = new CanvasService();
